// #4616 — how much of the boot read-priority window the app spent in the background.
//
// The window's 180 s failsafe reports a boot that never finished, and could not say
// whether the user had simply backgrounded the app — where the browser stops drawing
// and a boot legitimately pauses. This counter is what lets the report say it.
//
// It ONLY counts. A backgrounded boot is still reported, so no evidence is hidden;
// that is the deliberate difference from the L8 nav-restore watchdog, which does
// suppress while hidden. Both decisions: design-docs/boot-sequence.md, "#4616".
//
// Pure on purpose — no `document`, no timers, the time is passed in — so it runs under
// plain node in its unit test, the same way nav-restore-coordination.ts's helpers do.
// The caller owns the `visibilitychange` subscription and the clock.
export class BootWindowVisibility {
  private hiddenMsBefore = 0;
  private hiddenSinceMs: number | null;

  // `nowMs` and every later time must come from the SAME monotonic clock
  // (`performance.now()`), never the wall clock: the wall clock can be stepped by the
  // operating system mid-boot, which would make a stretch shorter or longer than it was.
  constructor(nowMs: number, startsHidden: boolean) {
    this.hiddenSinceMs = startsHidden ? nowMs : null;
  }

  // one `visibilitychange` event. Repeats in the same direction are ignored, so a
  // browser that fires hidden twice cannot restart or double-count a stretch.
  onVisibilityChanged(hidden: boolean, nowMs: number): void {
    // if going hidden while already hidden, the stretch is already running
    if (hidden) {
      if (this.hiddenSinceMs === null) {
        this.hiddenSinceMs = nowMs;
      }
      return;
    }
    // if becoming visible while already visible, there is no stretch to close
    if (this.hiddenSinceMs === null) {
      return;
    }
    this.hiddenMsBefore += Math.max(0, nowMs - this.hiddenSinceMs);
    this.hiddenSinceMs = null;
  }

  // total background time so far, including a stretch that is still running.
  // Clamped at 0 so a clock that does not advance can never report negative time.
  hiddenMsSoFar(nowMs: number): number {
    const ongoing = this.hiddenSinceMs === null ? 0 : Math.max(0, nowMs - this.hiddenSinceMs);
    return this.hiddenMsBefore + ongoing;
  }
}
